import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import PageHeader from '../../common/PageHeader'
import Section, { SubSection } from '../../common/Section'
import Alert from '../../common/Alert'
import CodeBlock, { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import { ParamTable } from '../../common/Table'
import MapIcon from '@mui/icons-material/Map'
import SearchIcon from '@mui/icons-material/Search'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import { useLocale } from '../../../i18n/useLocale'

export default function ApmFinderWidget() {
  const { urls, examples, address, locale, content, brand } = useLocale()

  return (
    <Box>
      <PageHeader
        title="APM Finder Widget"
        description="Embed an interactive map widget to let customers select their preferred parcel pickup location."
        breadcrumbs={[
          { label: 'Widgets', path: '/widgets' },
          { label: 'APM Finder' },
        ]}
      />

      <Section title="Overview" divider={false}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {content.apmFinder.description} Customers can search, browse, and select their preferred pickup location directly from
          your checkout page.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <MapIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">Interactive Map</Typography>
              <Typography variant="body2" color="text.secondary">
                Leaflet-powered map with custom markers for each APM location
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">Search & Filter</Typography>
              <Typography variant="body2" color="text.secondary">
                Search by name, address, city, or APM code with real-time filtering, plus an
                address geocoder on the map
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TouchAppIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">Selection Events</Typography>
              <Typography variant="body2" color="text.secondary">
                Receive selected location data via postMessage API
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Section>

      <Section title="Quick Start">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Add the widget to your page with a simple iframe:
        </Typography>

        <SimpleCodeBlock
          code={`<iframe
  src="${urls.widgetBase}"
  width="100%"
  height="600"
  frameborder="0"
  allow="geolocation"
  title="Select Pickup Location"
></iframe>`}
        />

        <Alert type="info" title="Responsive Design">
          The widget is fully responsive. Set a fixed height (recommended 600-800px) and let the width adapt to your container.
        </Alert>
      </Section>

      <Section title="Embedding Methods">
        <SubSection title="Method 1: Basic iframe">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Simplest integration - just add the iframe to your checkout page:
          </Typography>

          <CodeBlock
            title="Basic iframe Embed"
            examples={[
              {
                language: 'html',
                label: 'HTML',
                code: `<div class="apm-widget-container">
  <iframe
    id="apm-finder"
    src="${urls.widgetBase}"
    width="100%"
    height="600"
    frameborder="0"
    allow="geolocation"
    title="Select Pickup Location"
  ></iframe>
</div>

<style>
  .apm-widget-container {
    width: 100%;
    max-width: 800px;
    margin: 20px auto;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
  }
</style>`,
              },
            ]}
          />
        </SubSection>

        <SubSection title="Method 2: Responsive with Event Handling">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Full integration with location selection handling:
          </Typography>

          <CodeBlock
            title="Responsive iframe with Events"
            examples={[
              {
                language: 'html',
                label: 'HTML + JavaScript',
                code: `<div class="widget-wrapper">
  <iframe
    id="apm-finder"
    src="${urls.widgetBase}"
    width="100%"
    height="100%"
    frameborder="0"
    allow="geolocation"
    title="Select Pickup Location"
  ></iframe>
</div>

<div id="selected-location" style="display: none;">
  <h4>Selected Location:</h4>
  <p id="location-name"></p>
  <p id="location-address"></p>
</div>

<style>
  .widget-wrapper {
    width: 100%;
    height: 800px; /* Mobile height */
  }

  @media (min-width: 768px) {
    .widget-wrapper {
      height: 600px; /* Desktop height */
    }
  }
</style>

<script>
  window.addEventListener('message', function(event) {
    // IMPORTANT: Validate origin in production
    // if (event.origin !== '${urls.widgetBase}') return;

    if (event.data.type === 'LOCATION_SELECTED') {
      const location = event.data.data;
      console.log('Selected APM:', location);

      // Update your form
      document.getElementById('selected-location').style.display = 'block';
      document.getElementById('location-name').textContent = location.name;
      document.getElementById('location-address').textContent = location.address;

      // Store the APM ID for your API call
      document.getElementById('destination-apm').value = location.id;
    }
  });
</script>`,
              },
            ]}
          />
        </SubSection>
      </Section>

      <Section title="postMessage Events">
        <Typography variant="body1" sx={{ mb: 2 }}>
          When a customer selects a location, the widget sends a <InlineCode>LOCATION_SELECTED</InlineCode> message
          to the parent window:
        </Typography>

        <SubSection title="Event Structure">
          <SimpleCodeBlock
            code={`{
  "type": "LOCATION_SELECTED",
  "data": {
    "id": "${locale.countryCode.toLowerCase()}0001",
    "boxCode": "${locale.countryCode.toLowerCase()}0001",
    "name": "${examples.apmName}",
    "address": "${examples.apmLocation}",
    "latitude": ${examples.coordinates.latitude},
    "longitude": ${examples.coordinates.longitude}
  }
}`}
          />
        </SubSection>

        <SubSection title="Event Data Fields">
          <ParamTable
            params={[
              { name: 'id', type: 'string', required: true, description: 'APM code (same value as boxCode). Use this as the destination field when creating parcels — it identifies the whole machine; the individual locker is assigned by the system on delivery.' },
              { name: 'boxCode', type: 'string', required: true, description: `APM code (e.g., "${locale.countryCode.toLowerCase()}0001"). The field name is historical; the value identifies the whole APM, not a specific locker.` },
              { name: 'name', type: 'string', required: true, description: 'Human-readable APM name' },
              { name: 'address', type: 'string', required: true, description: 'Formatted address (addressLine, city)' },
              { name: 'latitude', type: 'number', required: true, description: 'GPS latitude coordinate' },
              { name: 'longitude', type: 'number', required: true, description: 'GPS longitude coordinate' },
            ]}
          />
        </SubSection>

        <SubSection title="React Integration Example">
          <CodeBlock
            title="React Hook for APM Selection"
            examples={[
              {
                language: 'jsx',
                label: 'React',
                code: `import { useEffect, useState } from 'react';

function useApmSelection() {
  const [selectedApm, setSelectedApm] = useState(null);

  useEffect(() => {
    const handleMessage = (event) => {
      // Validate origin in production
      // if (event.origin !== '${urls.widgetBase}') return;

      if (event.data.type === 'LOCATION_SELECTED') {
        setSelectedApm(event.data.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return selectedApm;
}

// Usage in component
function CheckoutPage() {
  const selectedApm = useApmSelection();

  const handleSubmit = async () => {
    if (!selectedApm) {
      alert('Please select a pickup location');
      return;
    }

    await createParcel({
      ...orderData,
      destination: selectedApm.id,  // Use the APM ID
    });
  };

  return (
    <div>
      <h3>Select Pickup Location</h3>
      <iframe
        src="${urls.widgetBase}"
        width="100%"
        height="500"
        frameBorder="0"
      />

      {selectedApm && (
        <div className="selected-location">
          <p>Selected: {selectedApm.name}</p>
          <p>{selectedApm.address}</p>
        </div>
      )}

      <button onClick={handleSubmit}>Continue</button>
    </div>
  );
}`,
              },
            ]}
          />
        </SubSection>
      </Section>

      <Section title="Configuration Options">
        <SubSection title="URL Parameters">
          <ParamTable
            params={[
              { name: 'selectable', type: 'boolean', required: false, description: 'Set to false for view-only mode (default: true)' },
              { name: 'lang', type: 'string', required: false, description: 'Language for localized texts (find-me directions, opening hours), e.g. "en" (default)' },
            ]}
          />

          <Typography variant="body2" sx={{ mt: 2 }}>
            Example: View-only map (no selection events):
          </Typography>
          <SimpleCodeBlock code={`${urls.widgetBase}?selectable=false&lang=en`} />
        </SubSection>
      </Section>

      <Section title="APM Data Structure">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Each APM location includes the following data. The same data the widget uses is served by
          the public APM feed (<InlineCode>GET {urls.apiBase}/apm</InlineCode>, no auth):
        </Typography>

        <SimpleCodeBlock
          code={`{
  "name": "${examples.apmName}",
  "boxCode": "${locale.countryCode}0001",
  "address": {
    "country": "${locale.countryCode}",
    "addressLine": "${address.addressLineExample}",
    "city": "${address.cityExample}",
    "postalCode": "${address.postalCodeExample}",
    "poBoxNumber": null
  },
  "latitude": ${examples.coordinates.latitude},
  "longitude": ${examples.coordinates.longitude},
  "isOutdoor": false,
  "apmLoad": 0.42,
  "openingHours": { "EN": "Mon-Sun 06:00-22:00" },
  "findme": { "EN": "${examples.apmFindMe}" }
}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Notes: <InlineCode>findme</InlineCode> and <InlineCode>openingHours</InlineCode> are maps
          keyed by language code; <InlineCode>apmLoad</InlineCode> is the current fill indicator.
          The feed contains active, locatable machines only — there is no <InlineCode>id</InlineCode>,{' '}
          <InlineCode>status</InlineCode> or <InlineCode>apmType</InlineCode> field. Use{' '}
          <InlineCode>boxCode</InlineCode> as the destination value when creating parcels.
        </Typography>
      </Section>

      <Section title="Styling & Customization">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The widget includes built-in responsive design:
        </Typography>

        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li><strong>Mobile:</strong> Vertical layout with list above map</li>
          <li><strong>Desktop:</strong> Sidebar list next to map (split view)</li>
          <li><strong>Dark Mode:</strong> Follows the visitor's system preference automatically</li>
          <li><strong>Primary Color:</strong> {brand.name} brand ({brand.primaryColor}) — fixed; there are currently no runtime theming or color parameters</li>
        </Box>
      </Section>

      <Section title="Security Considerations">
        <Alert type="warning" title="Origin Validation">
          The widget posts its messages with <InlineCode>targetOrigin: "*"</InlineCode> — it does not
          restrict who can receive them. Your listener is therefore the only guard: in production,
          always validate the message origin to prevent spoofed events:
        </Alert>

        <SimpleCodeBlock
          code={`window.addEventListener('message', function(event) {
  // Only accept messages from the widget origin
  if (event.origin !== '${urls.widgetBase}') {
    return;  // Ignore messages from other sources
  }

  if (event.data.type === 'LOCATION_SELECTED') {
    // Safe to process
    handleLocationSelected(event.data.data);
  }
});`}
        />
      </Section>

      <Section title="Troubleshooting">
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 2 } }}>
          <li>
            <strong>Widget not loading:</strong> Check that the iframe src URL is correct and accessible.
            Verify there are no Content-Security-Policy headers blocking the embed.
          </li>
          <li>
            <strong>No locations showing:</strong> The feed is public and account-independent; the
            widget only hides APMs without map coordinates. If nothing shows, check network access
            to the APM feed endpoint.
          </li>
          <li>
            <strong>Selection events not received:</strong> Verify your message event listener is set up
            before the iframe loads. Check browser console for errors.
          </li>
          <li>
            <strong>Map tiles or search not loading:</strong> The widget loads OpenStreetMap tiles and
            uses the OSM geocoder — verify no firewall rules block{' '}
            <InlineCode>tile.openstreetmap.org</InlineCode> and{' '}
            <InlineCode>nominatim.openstreetmap.org</InlineCode> (fonts come from Google Fonts).
          </li>
        </Box>
      </Section>
    </Box>
  )
}
